import { uiAnimate, UIAnimation } from './animate';
import { uiTrackAttach } from './attach';
import { uiIsMedia } from './media';
import { UIClassesSpec } from './types';
import { UIView } from './view';

export type UIBackdropKind = 'dark' | 'darker' | 'none';
export type UIPresentationPosition = 'center' | 'overlay' | 'top' | 'right' | 'left';

export interface UIPresentationOptions {
    on_click?: (e: MouseEvent) => void;
    on_wheel?: (e: MouseEvent) => void;
    backdrop_kind?: UIBackdropKind;
    backdrop_classes?: UIClassesSpec;
    presenter_classes?: UIClassesSpec;
    position?: UIPresentationPosition;
    replace?: UIView | true;
    replace_back?: boolean;
    low?: boolean;
}

class UIBackdrop extends UIView {
    constructor(depth: number, view: UIView, options: UIPresentationOptions) {
        super('div', ['ui-backdrop', options.backdrop_kind && 'k-' + options.backdrop_kind, options.backdrop_classes]);
        let base_z = options.low ? 300 : 500;
        if(view.classList.contains('m-mobile-fs'))
            base_z = 2147483099;
        this.root.style.zIndex = '' + (base_z + 2 * depth);
    }
}

class UIPresenter extends UIView {
    constructor(depth: number, view: UIView, options: UIPresentationOptions) {
        super('aside', ['ui-presenter x-context a-' + (options.position || 'center'), options.presenter_classes], view);
        let base_z = options.low ? 301 : 501;
        if(view.classList.contains('m-mobile-fs'))
            base_z = 2147483100;
        this.root.style.zIndex = '' + (base_z + 2 * depth);

        if(options.on_click) {
            this.root.addEventListener('click', (e) => {
                if(e.target === this.root) {
                    options!.on_click!(e);
                }
            });
        }

        if(options.on_wheel) {
            const elem = (view as any).root as HTMLElement;
            elem.addEventListener('wheel', (e) => {
                elem.scrollTop += e.deltaY;
                elem.scrollLeft += e.deltaX;
                e.preventDefault();
            });

            this.root.addEventListener('wheel', (e) => {
                e.preventDefault();
                if(e.target === this.root) {
                    options!.on_wheel!(e);
                }
            });
        }
    }
}

interface UIPresentation {
    view: UIView;
    presenter: UIPresenter;
    backdrop?: UIBackdrop;
    position?: UIPresentationPosition;
}

const presentation_stack: UIPresentation[] = [];

function animatePresentation(in_presentation?: UIPresentation, out_presentation?: UIPresentation, back?: boolean) {
    const animations: UIAnimation[] = [];
    let completion: (() => void) | undefined;

    if(in_presentation) {
        const position = in_presentation.position;
        const view = in_presentation.view;
        const backdrop = in_presentation.backdrop;
        const presenter = in_presentation.presenter;

        if(!out_presentation && backdrop) {
            animations.push({ what: backdrop, animation: 'fade-in', duration: 0.3 });
        }

        if(position === 'center') {
            if(out_presentation && out_presentation.position === 'center') {
                animations.push({ what: presenter, animation: back ? 'slide-right' : 'slide-left', duration: 0.4, easing: 'ease-in-out' });
            } else {
                if(view.classList.contains('m-mobile-fs') && uiIsMedia('m'))
                    animations.push({ what: view, animation: 'slide-bottom', duration: 0.25 });
                else
                    animations.push({ what: view, animation: 'ui-pan', duration: 0.4 });
            }
        } else if(position === 'overlay') {
            animations.push({ what: view, animation: 'fade-in', duration: 0.3 });
        } else if(position === 'top') {
            animations.push({ what: view, animation: 'fade-in', duration: 0.2 });
        } else if(position === 'right') {
            animations.push({ what: view, animation: 'slide-left', duration: 0.3 });
        } else if(position === 'left') {
            animations.push({ what: view, animation: 'slide-right', duration: 0.3 });
        }

        document.body.ui_add([backdrop, presenter]);
    }

    if(out_presentation) {
        const position = out_presentation.position;
        const view = out_presentation.view;
        const backdrop = out_presentation.backdrop;
        const presenter = out_presentation.presenter;

        presenter.classList.add('f-dismissing');

        if(!in_presentation && backdrop) {
            animations.push({ what: backdrop, animation: 'fade-out', duration: 0.2 });
            backdrop.ui_elem.style.opacity = '0';
        }

        if(position === 'center') {
            if(in_presentation && in_presentation.position === 'center') {
                animations.push({ what: presenter, animation: back ? 'slide-left' : 'slide-right', reverse: true, duration: 0.4, easing: 'ease-in-out' });
            } else {
                if(view.classList.contains('m-mobile-fs') && uiIsMedia('m'))
                    animations.push({ what: view, animation: 'slide-bottom', reverse: true, duration: 0.2 });
                else {
                    animations.push({ what: view, animation: 'ui-pan', reverse: true, duration: 0.2 });
                    view.ui_elem.style.opacity = '0';
                }
            }
        } else if(position === 'overlay') {
            animations.push({ what: view, animation: 'fade-in', reverse: true, duration: 0.2 });
            (view as any).root.style.opacity = 0;
        } else if(position === 'right') {
            animations.push({ what: view, animation: 'slide-left', reverse: true, duration: 0.3 });
        } else if(position === 'left') {
            animations.push({ what: view, animation: 'slide-right', reverse: true, duration: 0.3 });
        } else if(position === 'top') {
            animations.push({ what: view, animation: 'fade-in', reverse: true, duration: 0.2 });
            (view as any).root.style.opacity = 0;
        }

        completion = () => {
            if(!in_presentation && backdrop)
                backdrop.remove();
            presenter.remove();
        }
    }

    uiAnimate(animations, completion);
}

export function uiPresent(view: UIView, options: UIPresentationOptions) {
    document.documentElement.classList.add('f-aside');

    const presenter = new UIPresenter(presentation_stack.length, view, options);

    if(options.replace) {
        const index = (options.replace === true) ? (presentation_stack.length - 1) : presentation_stack.findIndex(p => p.view === options.replace);
        if(index >= 0) {
            const old_presentation = presentation_stack[index];
            const new_presentation = { view: view, presenter: presenter, backdrop: old_presentation.backdrop, position: options.position }

            animatePresentation(new_presentation, old_presentation, options.replace_back);

            presentation_stack[index] = new_presentation;
            return;
        }
    }

    const backdrop = options.backdrop_kind == 'none' ? undefined : new UIBackdrop(presentation_stack.length, view, options);
    const presentation = { view: view, presenter: presenter, backdrop: backdrop, position: options.position };

    animatePresentation(presentation);

    presentation_stack.push(presentation);

    // function preventDefault(e: Event) {
    //     e.preventDefault();
    // }

    // presenter.ui_elem.addEventListener('pointermove', preventDefault);
    // presenter.ui_elem.addEventListener('touchmove', preventDefault);

    uiTrackAttach(true);
}

export function uiDismissView(view: UIView) {
    const index = presentation_stack.findIndex(p => p.view === view);
    if(index < 0)
        throw new Error('Dismissing invalid view of type ' + view.constructor.name);

    const presentation = presentation_stack.splice(index, 1)[0];

    if(!presentation_stack.length)
        document.documentElement.classList.remove('f-aside');

    animatePresentation(undefined, presentation);
}
